import { Box, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BarPlot } from '@mui/x-charts/BarChart';
import { ChartsContainer } from '@mui/x-charts/ChartsContainer';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';

import {
  formatNumber,
  toHumanReadableDate,
} from '@modules/_shared/utils/formatting';
import type { IntlLocale } from '@shared/LocaleContext';

export type DailyOpen = { date: string; total: number; unique: number };
type ValueFormatterContext = { dataIndex?: number };

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const formatAxisDate = (date: string, locale: IntlLocale): string => {
  try {
    return toHumanReadableDate(locale, date, {
      day: 'numeric',
      month: 'short',
      year: undefined,
      timeZone: 'UTC',
    });
  } catch {
    return date;
  }
};

export const buildStatsRange = (
  rangeDays: number,
): { from: string; to: string } => {
  const now = new Date();
  const start = new Date(now.getTime() - (rangeDays - 1) * MS_PER_DAY);
  start.setUTCHours(0, 0, 0, 0);
  return { from: start.toISOString(), to: now.toISOString() };
};

export const buildChartData = (
  daily: readonly DailyOpen[],
  rangeDays: number,
  anchorIso: string,
): DailyOpen[] => {
  const byDate = new Map(daily.map(d => [d.date, d] as const));
  const anchor = new Date(anchorIso);
  return Array.from({ length: rangeDays }, (_, index) => {
    const offset = rangeDays - 1 - index;
    const date = new Date(anchor.getTime() - offset * MS_PER_DAY)
      .toISOString()
      .slice(0, 10);
    const row = byDate.get(date);
    return { date, total: row?.total ?? 0, unique: row?.unique ?? 0 };
  });
};

export const OpensBarChart = ({
  data,
  totalOpensLabel,
  uniqueOpensLabel,
  locale,
  height = 160,
  showUniqueSplit = true,
}: {
  data: DailyOpen[];
  totalOpensLabel: string;
  uniqueOpensLabel: string;
  locale: IntlLocale;
  height?: number;
  // When false, render a single "total views" series instead of splitting into
  // unique visitors + repeat opens (used for the per-buyer chart, where a single
  // recipient's unique/repeat split is not meaningful).
  showUniqueSplit?: boolean;
}) => {
  const theme = useTheme();

  const labels = data.map(d => formatAxisDate(d.date, locale));
  const tickStep = Math.max(1, Math.ceil(labels.length / 5));
  const totals = data.map(d => d.total);
  const uniques = data.map(d => d.unique);
  const repeats = data.map(d => Math.max(0, d.total - d.unique));
  const formatTotal = (_value: number | null, ctx: ValueFormatterContext) =>
    formatNumber(totals[ctx.dataIndex ?? -1] ?? 0, locale);

  const series = showUniqueSplit
    ? [
        {
          type: 'bar' as const,
          stack: 'opens',
          data: uniques,
          label: uniqueOpensLabel,
          color: theme.palette.primary.main,
          valueFormatter: (
            _value: number | null,
            ctx: { dataIndex?: number },
          ) => formatNumber(uniques[ctx.dataIndex ?? -1] ?? 0, locale),
        },
        {
          type: 'bar' as const,
          stack: 'opens',
          data: repeats,
          label: totalOpensLabel,
          color: alpha(theme.palette.primary.main, 0.35),
          valueFormatter: formatTotal,
        },
      ]
    : [
        {
          type: 'bar' as const,
          data: totals,
          label: totalOpensLabel,
          color: theme.palette.primary.main,
          valueFormatter: formatTotal,
        },
      ];

  return (
    <Box sx={{ width: '100%', height }}>
      <ChartsContainer
        height={height}
        margin={{ top: 16, right: 12, bottom: 8, left: 8 }}
        series={series}
        xAxis={[
          {
            id: 'days',
            data: labels,
            scaleType: 'band',
            position: 'bottom',
            categoryGapRatio: 0.25,
            height: 24,
            tickLabelStyle: { fontSize: 10 },
            tickLabelInterval: (_value, index) =>
              (index % tickStep === 0 && index < labels.length - 1) ||
              index === labels.length - 1,
          },
        ]}
        yAxis={[
          {
            id: 'opens',
            scaleType: 'linear',
            position: 'left',
            min: 0,
            tickMinStep: 1,
            tickNumber: 4,
            tickLabelStyle: { fontSize: 10 },
            width: 32,
          },
        ]}
      >
        <ChartsGrid horizontal />
        <BarPlot borderRadius={3} />
        <ChartsXAxis axisId="days" disableLine disableTicks />
        <ChartsYAxis axisId="opens" disableLine disableTicks />
        <ChartsTooltip trigger="axis" />
      </ChartsContainer>
    </Box>
  );
};
